import {
  type OrgStaffMember,
  organizationStaffQueryKey,
  useOrganizationStaffQuery,
  useUpdateSchoolReferralContactMutation,
  useUpdateUserPhoneNumberMutation,
} from '@cartwheel/api/v1'
import { type ReferralContact, SchoolReferralContactPicker } from '@cartwheel/components'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

interface Props {
  organizationId: string
  institutionId: string
  pointOfContact: OrgStaffMember | null
}

function mapContact(member: OrgStaffMember): ReferralContact {
  return {
    id: member.id,
    name: member.name,
    title: member.title,
    avatarUrl: member.avatar_url,
    phoneNumber: member.phone_number,
  }
}

/**
 * Single-school referral-contact control for the school edit page — the per-school
 * counterpart to the organization-wide bulk table. Kept separate from the school's
 * "Additional approvers" control (approvers ≠ referral contact).
 */
export default function SchoolReferralContactField({
  organizationId,
  institutionId,
  pointOfContact: initialContact,
}: Props) {
  const queryClient = useQueryClient()
  const [contact, setContact] = useState<OrgStaffMember | null>(initialContact)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useOrganizationStaffQuery({
    path: { organization_id: organizationId },
  })
  const staff = useMemo(() => (data?.staff ?? []).map(mapContact), [data])
  const districtDefaults = useMemo(
    () => (data?.district_default_contacts ?? []).map(mapContact),
    [data]
  )

  const assignMutation = useUpdateSchoolReferralContactMutation()
  const phoneMutation = useUpdateUserPhoneNumberMutation()

  async function handleSelect(userId: string | null) {
    setError(null)
    try {
      await assignMutation.mutateAsync({
        path: { organization_id: organizationId, id: institutionId },
        body: { institution: { point_of_contact_id: userId } },
      })
      setContact(userId ? (data?.staff.find((m) => m.id === userId) ?? null) : null)
    } catch {
      setError("Couldn't update the referral contact. Please try again.")
    }
  }

  async function handleSavePhone(userId: string, digits: string) {
    setError(null)
    try {
      await phoneMutation.mutateAsync({
        path: { id: userId },
        body: { user: { phone_number: digits } },
      })
      setContact((prev) => (prev && prev.id === userId ? { ...prev, phone_number: digits } : prev))
      queryClient.invalidateQueries({
        queryKey: organizationStaffQueryKey({ path: { organization_id: organizationId } }),
      })
    } catch {
      setError("Couldn't save the phone number. Please try again.")
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <SchoolReferralContactPicker
        value={contact ? mapContact(contact) : null}
        staff={staff}
        districtDefaultContacts={districtDefaults}
        onSelect={handleSelect}
        onSavePhone={handleSavePhone}
        isSaving={assignMutation.isPending || phoneMutation.isPending}
        disabled={isLoading}
      />
      {error && <p className="text-sm text-brick-light">{error}</p>}
    </div>
  )
}
